import { Select } from '@components/core-ui/form-elements/Select';
import { Pager } from './components/pager';
import { cn } from '@helpers/cn';
import { TProps } from './types/TProps';

const paginationSelectWrapperClassName = 'inline-flex h-10 w-[70px] max-w-[70px] shrink-0';

const paginationSelectTriggerClassName =
  'select relative flex h-10 w-[70px] max-w-[70px] shrink-0 flex-none cursor-pointer flex-row-reverse items-center justify-evenly rounded-[5px] border-none bg-[var(--select-field-bg,var(--color-form-field-bg,var(--color-surface-muted)))] p-0 hover:shadow-[0_0_0_1px_var(--color-link)] hover:outline-none focus:outline-none focus:shadow-[0_0_0_1px_var(--color-link)]';

const paginationSelectIconClassName = [
  'icon icon--closed m-0 inline-flex h-4 w-4 shrink-0 items-center justify-center self-center border-0 bg-transparent p-0.5 cursor-pointer leading-none hover:text-[var(--color-link)] [&_svg]:block [&_svg]:h-2 [&_svg]:w-2.5',
  '!text-[var(--color-form-field-icon,var(--color-text-muted))] enabled:hover:!text-[var(--color-form-field-icon,var(--color-text-muted))]',
].join(' ');

const paginationSelectLabelClassName = [
  'label static m-0 whitespace-nowrap text-lg font-semibold leading-[150%]',
  '!text-[var(--color-form-field-text,var(--color-text-muted))]',
].join(' ');

const paginationSelectOptionsWrapperClassName = 'leading-[17px]';

const paginationSelectDropdownClassName = [
  '[&_.select:hover_.label]:text-[var(--color-link)]',
  '[&_.select:focus_.label]:text-[var(--color-link)]',
  '[&_.optionsLabel]:hidden',
  '[&_.option]:flex [&_.option]:h-10 [&_.option]:items-center [&_.option]:justify-center [&_.option]:whitespace-nowrap [&_.option]:px-0 [&_.option]:py-0 [&_.option]:text-lg [&_.option]:font-semibold [&_.option]:leading-[150%] [&_.option]:text-[var(--color-form-field-text,var(--color-text-muted))]',
].join(' ');

export const PagePagination = ({
  totalItemsCount,
  page,
  onPageChange,
  pageSize,
  setPageSize,
  embedded = false,
}: TProps) => {
  const totalPages = Math.ceil((totalItemsCount || 1) / pageSize);

  const getShowingPagesText = () => {
    if (!totalItemsCount) {
      return 'Showing 0 of 0';
    }
    const start = ((page ?? 1) - 1) * pageSize + 1;
    const end = Math.min(start + pageSize - 1, totalItemsCount);
    return `Showing ${start} - ${end} of ${totalItemsCount}`;
  };

  return (
    <div className={cn('page-pagination', embedded ? 'page-pagination--embedded' : 'page-pagination--page')}>
      {totalPages ? (
        <>
          <div className="flex min-w-0 flex-wrap items-center gap-2 pb-2">
            <span className="shrink-0 whitespace-nowrap text-sm tracking-wide text-foreground-muted">
              Rows per page
            </span>
            <Select
              name="rowsPerPage"
              compact
              className={`${paginationSelectWrapperClassName} ${paginationSelectDropdownClassName}`}
              triggerClassName={paginationSelectTriggerClassName}
              iconClassName={paginationSelectIconClassName}
              labelClassName={paginationSelectLabelClassName}
              optionsWrapperClassName={paginationSelectOptionsWrapperClassName}
              placement="top-start"
              label={String(pageSize)}
              value={pageSize}
              onChange={(_e, value) => setPageSize(+value)}
              options={[
                { value: 10, label: '10' },
                { value: 25, label: '25' },
                { value: 50, label: '50' },
                { value: 100, label: '100' },
              ]}
            />
            <span className="shrink-0 whitespace-nowrap pl-3 font-['General_Sans',sans-serif] text-sm font-normal leading-[150%] tracking-[0.03em] text-[var(--color-text-muted)]">
              {getShowingPagesText()}
            </span>
          </div>

          <div className="paginationBlock">
            <Pager
              currentPage={page}
              pagesCount={totalPages}
              onChange={(nextPage: number) => onPageChange(Math.max(1, nextPage))}
            />
          </div>
        </>
      ) : null}
    </div>
  );
};
